import { format, formatDistance, parseISO, parseJSON } from 'date-fns'
import { enGB, lv, ru } from 'date-fns/locale'
import I18n from 'i18n'
import padStart from 'lodash/padStart'
import { TimeFormat } from 'schema'
import floor from 'lodash/floor'

const decimalStrRegExp = /[^\d.]/g
const timeFormatRegExp = /[^\d:]/g

const locales: Record<typeof I18n.locale, typeof enGB> = {
  en: enGB,
  lv: lv,
  ru: ru
}

const blankValue = '-'

export default function formatTime(
  minutesInput: number | undefined | null,
  format: TimeFormat = TimeFormat.HhMmWithSeperator,
  diurnal?: boolean
): string {
  if (isNaN(Number(minutesInput))) {
    return ''
  }

  let minutesCalc = minutesInput ?? 0

  if (diurnal === true) {
    minutesCalc = minutesCalc > 1440 ? 1440 : minutesCalc
  }

  const hours = Math.floor(minutesCalc / 60)
  const minutes = Math.floor(minutesCalc % 60)
  const displayableMinutes =
    floor(minutes / 60, 1)
      .toFixed(2)
      .split('.')[1] ?? ''

  switch (format) {
    case TimeFormat.Decimal:
      return `${hours}.${displayableMinutes[1] === '0' ? displayableMinutes[0] : displayableMinutes}`
    case TimeFormat.HhMmWithSeperator:
      return `${hours}:${padStart(minutes.toString(), 2, '0')}`
    case TimeFormat.HhMmWithLabel:
      return `${hours}h ${padStart(minutes.toString(), 2, '0')}m`
    default:
      return `${hours}:${padStart(minutes.toString(), 2, '0')}`
  }
}

export function minutesFromTimeString(timeStr: string, timeFormat?: TimeFormat): number {
  let hoursStr: string
  let minutesRaw: string | number

  if (timeStr === '') {
    ;[hoursStr, minutesRaw] = ['0', '0']
  } else if (timeStr.includes(':')) {
    ;[hoursStr, minutesRaw] = timeStr.split(':')
  } else if (timeStr.includes('.')) {
    ;[hoursStr, minutesRaw] = timeStr.split('.')

    minutesRaw = parseFloat('0.' + minutesRaw) * 60
  } else if (timeStr.includes(',')) {
    ;[hoursStr, minutesRaw] = timeStr.split(',')

    minutesRaw = parseFloat('0.' + minutesRaw) * 60
  } else if (timeStr.includes('h')) {
    ;[hoursStr, minutesRaw] = timeStr.replace(/\s+/g, '').replace('m', '').split('h')
  } else if (timeStr.includes('m')) {
    hoursStr = '0'
    minutesRaw = timeStr.replace(/\s+/g, '').replace('m', '')
  } else if (timeFormat === TimeFormat.HhMmWithSeperator) {
    ;[hoursStr, minutesRaw] = [timeStr.slice(-4, -2), timeStr.slice(-2)]
  } else {
    return stringToMinutes(timeStr)
  }

  const hourMinutes = parseInt(hoursStr) * 60
  const minutes = typeof minutesRaw === 'string' ? parseInt(minutesRaw) : minutesRaw

  return (isNaN(hourMinutes) ? 0 : hourMinutes) + (isNaN(minutes) ? 0 : minutes)
}

export function stringToMinutes(inputStr: string): number {
  const decimalStr = inputStr.replace(decimalStrRegExp, '')
  return parseFloat(decimalStr) * 60
}

export function formatTimeString(inputStr: string, format: TimeFormat): string {
  const cleanTimeStr = inputStr.replace(timeFormatRegExp, '')

  return formatTime(minutesFromTimeString(cleanTimeStr), format)
}

export const ensureDate = (maybeDate: string | Date): Date => {
  let date!: Date

  if (maybeDate.constructor === Date) {
    date = maybeDate
  } else if (maybeDate.constructor === String) {
    date = maybeDate.includes('T') ? parseJSON(maybeDate) : parseISO(maybeDate)
  }

  return date
}

export const formatDate = (maybeDate: string | Date | undefined, outputFormat: string): string => {
  try {
    if (!maybeDate) {
      return blankValue
    }

    const date = ensureDate(maybeDate)

    return format(date, outputFormat, { locale: locales[I18n.locale] })
  } catch (error) {
    return blankValue
  }
}

export const timeAgo = (maybeDate: string | Date, addSuffix = true) => {
  try {
    const date = ensureDate(maybeDate)

    return formatDistance(date, new Date(), { addSuffix, locale: locales[I18n.locale] })
  } catch (error) {
    return blankValue
  }
}

export const daysAgo = (daysAgo: number | undefined | null) => {
  if (!daysAgo) {
    return
  }

  try {
    const prior = new Date().setDate(new Date().getDate() - daysAgo)

    return new Date(prior)
  } catch (error) {
    return
  }
}
